/**
 * edgeFinishRenderer.js
 * ----------------------------------------------------------------------------
 * Tradução do ACABAMENTO DE BORDA (edgeFinishType + edgeFinishes) em estilo
 * visual de linha SVG.
 *
 * Atenção aos DOIS conceitos de "acabamento" do sistema:
 *  1. Taxa de acabamento (taxFinishing): % financeira. NÃO é desenhada aqui.
 *  2. Item type === "Acabamento": serviço separado. Vai para a LEGENDA, não vira
 *     borda. Tratado em drawingFactory/legend.
 *  Este arquivo cuida apenas do 3º caso: a BORDA da peça com acabamento
 *  (edgeFinishType/edgeFinishes), que é puramente visual.
 *
 * MVP: não desenha o acabamento "perfeito" de cada tipo, apenas diferencia o
 * traço. A arquitetura abaixo (descriptor por tipo) permite evoluir cada estilo
 * sem tocar no canvas — basta enriquecer o descriptor.
 */

import { getEdgeLine, getEdgeSegments } from "./measurementUtils";

// Borda com acabamento: mesmo tom do traço (P&B), diferenciada pela
// ESPESSURA + swatch/decoração — sem cores duras no desenho técnico.
const ACTIVE_COLOR = "#173b33";
const PLAIN_COLOR = "#173b33";

/**
 * Estilos por tipo de acabamento de borda.
 *  - stroke / strokeWidth / dash: aparência base da linha.
 *  - decoration: dica para o canvas desenhar marcadores extras no futuro
 *    ('rounded', 'double', 'hatch', 'drip'...). No MVP pode ser ignorado.
 */
export const EDGE_FINISH_STYLES = {
  polida: { stroke: ACTIVE_COLOR, strokeWidth: 1.4, dash: null, decoration: "solid" },
  boleada: { stroke: ACTIVE_COLOR, strokeWidth: 1.8, dash: null, decoration: "rounded" },
  bisote: { stroke: ACTIVE_COLOR, strokeWidth: 1.4, dash: "6 3", decoration: "double" },
  "meia-esquadria": { stroke: ACTIVE_COLOR, strokeWidth: 1.4, dash: null, decoration: "miter" },
  chanfrada: { stroke: ACTIVE_COLOR, strokeWidth: 1.4, dash: "2 3", decoration: "chamfer" },
  pingadeira: { stroke: ACTIVE_COLOR, strokeWidth: 1.8, dash: "1 4", decoration: "drip" }
};

/** Estilo da borda sem acabamento (linha normal). */
export const PLAIN_EDGE_STYLE = {
  stroke: PLAIN_COLOR,
  strokeWidth: 1,
  dash: null,
  decoration: "none"
};

/** Opções de acabamento com rótulo amigável (menus/inspector). */
export const EDGE_FINISH_OPTIONS = [
  { value: "polida", label: "Polida reta" },
  { value: "boleada", label: "Boleada" },
  { value: "bisote", label: "Bisote" },
  { value: "meia-esquadria", label: "Meia esquadria" },
  { value: "chanfrada", label: "Chanfrada" },
  { value: "pingadeira", label: "Pingadeira" }
];

// direção "para dentro" da peça, por borda (front = base do desenho)
const EDGE_INWARD = {
  front: { x: 0, y: -1 },
  back: { x: 0, y: 1 },
  left: { x: 1, y: 0 },
  right: { x: -1, y: 0 }
};

/**
 * Linhas EXTRAS que desenham o perfil do acabamento (a promessa do campo
 * `decoration`): linha dupla da boleada/bisote, tracinhos da pingadeira,
 * ticks 45° da meia-esquadria. Retorna segmentos prontos para SVG.
 * @returns {Array<{x1,y1,x2,y2,strokeWidth,dash?,linecap?}>}
 */
export function getEdgeDecorations(edge, line, type) {
  const n = EDGE_INWARD[edge] || EDGE_INWARD.front;
  const dx = line.x2 - line.x1;
  const dy = line.y2 - line.y1;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  // linha paralela deslocada `d`px para dentro da peça
  const inner = d => ({
    x1: line.x1 + n.x * d,
    y1: line.y1 + n.y * d,
    x2: line.x2 + n.x * d,
    y2: line.y2 + n.y * d
  });

  switch (type) {
    case "boleada":
      return [{ ...inner(3), strokeWidth: 1.4, linecap: "round" }];
    case "bisote":
      return [{ ...inner(3), strokeWidth: 1 }];
    case "chanfrada":
      return [{ ...inner(3), strokeWidth: 1, dash: "4 3" }];
    case "meia-esquadria": {
      const t = 7; // ticks a 45° nas duas pontas
      return [
        {
          x1: line.x1,
          y1: line.y1,
          x2: line.x1 + (ux + n.x) * t,
          y2: line.y1 + (uy + n.y) * t,
          strokeWidth: 1
        },
        {
          x1: line.x2,
          y1: line.y2,
          x2: line.x2 + (-ux + n.x) * t,
          y2: line.y2 + (-uy + n.y) * t,
          strokeWidth: 1
        }
      ];
    }
    case "pingadeira": {
      const ticks = [];
      const spacing = 9;
      const tick = 4;
      for (let d = spacing; d < len - 2; d += spacing) {
        const px = line.x1 + ux * d;
        const py = line.y1 + uy * d;
        ticks.push({
          x1: px,
          y1: py,
          x2: px + n.x * tick,
          y2: py + n.y * tick,
          strokeWidth: 1
        });
      }
      return ticks;
    }
    default:
      return [];
  }
}

/**
 * Retorna true se a peça tem acabamento na borda indicada.
 */
export function hasEdgeFinish(piece, edge) {
  return (
    Array.isArray(piece && piece.edgeFinishes) &&
    piece.edgeFinishes.includes(edge)
  );
}

/**
 * Descriptor de renderização de UMA borda. Consumido pelo DrawingCanvas.
 * @returns {{ edge, line, style, active }}
 */
export function renderEdgeFinish(piece, edge, bounds) {
  const active = hasEdgeFinish(piece, edge);
  const style = active
    ? EDGE_FINISH_STYLES[piece.edgeFinishType] || EDGE_FINISH_STYLES.polida
    : PLAIN_EDGE_STYLE;
  // silhueta L/U: uma borda lógica pode ter vários segmentos
  const lines = getEdgeSegments(piece, edge, bounds);

  return {
    edge,
    line: lines[0] || getEdgeLine(piece, edge, bounds),
    lines,
    style,
    active,
    // segmentos extras que desenham o PERFIL do acabamento
    decorations: active
      ? lines.reduce(
          (all, line) =>
            all.concat(
              getEdgeDecorations(edge, line, piece.edgeFinishType || "polida")
            ),
          []
        )
      : []
  };
}

/** Descriptors das 4 bordas de uma peça. */
export function renderAllEdges(piece, bounds) {
  return ["front", "back", "left", "right"].map(edge =>
    renderEdgeFinish(piece, edge, bounds)
  );
}
